/* eslint-disable @typescript-eslint/ban-ts-comment */
import { offlineExchange } from "@urql/exchange-graphcache";
import { makeDefaultStorage } from "@urql/exchange-graphcache/default-storage";
import type { ModelsResolver } from "@/models/resolvers";
import type { IntrospectionData } from "@urql/exchange-graphcache/dist/types/ast";
import schema from "gql/schema.min.json";

import scenarios from "@/models/scenarios/resolvers";
import sizings from "@/models/sizings/resolvers";
import performanceEvaluations from "@/models/performance_evaluations/resolvers";
import scenarioCorrelations from "@/models/scenarios/correlations/resolvers";
import scenarioEquipment from "@/models/scenarios/equipment/resolvers";
import xrefIntakes from "@/models/scenarios/equipment/xref_intakes/resolvers";
import xrefCables from "@/models/scenarios/equipment/xref_cables/resolvers";
import xrefPumps from "@/models/scenarios/equipment/xref_pumps/resolvers";
import xrefHousings from "@/models/scenarios/equipment/xref_housings/resolvers";
import xrefSeals from "@/models/scenarios/equipment/xref_seals/resolvers";
import xrefUpperYToolBypassTubings from "@/models/scenarios/equipment/xref_upper_y_tool_bypass_tubings/resolvers";
import xrefUpperYToolEquipmentTubings from "@/models/scenarios/equipment/xref_upper_y_tool_equipment_tubings/resolvers";
import xrefLowerYToolBypassTubings from "@/models/scenarios/equipment/xref_lower_y_tool_bypass_tubings/resolvers";
import xrefLowerYToolEquipmentTubings from "@/models/scenarios/equipment/xref_lower_y_tool_equipment_tubings/resolvers";
import scenarioParameters from "@/models/scenarios/parameters/resolvers";
import scenarioWellProperties from "@/models/scenarios/well_properties/resolvers";
import deviationSurvey from "@/models/deviation_surveys/resolvers";
import legacyImportedSizings from "@/models/legacy_imported_sizings/resolvers";

import type { GraphCacheConfig } from "gql/api";

export const urqlIdbStorage = makeDefaultStorage({
  idbName: "reaver-v1", // The name of the IndexedDB database (this should be renamed to spyglass when doing more offline work later)
  maxAge: 7 // The maximum age of the persisted data in days
});

// --------------------------------------
// --------------------------------------

// When adding a new model's resolver
// import into this file and add to the array below
const models: ModelsResolver[] = [
  sizings,
  scenarios,
  scenarioCorrelations,
  scenarioEquipment,
  xrefIntakes,
  xrefCables,
  xrefPumps,
  xrefHousings,
  xrefSeals,
  xrefUpperYToolBypassTubings,
  xrefUpperYToolEquipmentTubings,
  xrefLowerYToolBypassTubings,
  xrefLowerYToolEquipmentTubings,
  scenarioParameters,
  scenarioWellProperties,
  deviationSurvey,
  performanceEvaluations,
  legacyImportedSizings
];

// --------------------------------------
// --------------------------------------

// extracts resolver field from each model and joins into one object
const modelField = (key: keyof ModelsResolver) =>
  Object.assign({}, {}, ...models.map(model => model[key]));

// @ts-ignore
export const cache = offlineExchange<GraphCacheConfig>({
  schema: schema as unknown as IntrospectionData, // https://github.com/FormidableLabs/urql/discussions/1389
  storage: urqlIdbStorage,
  resolvers: modelField("queries"),
  optimistic: modelField("optimistic"),
  updates: {
    Mutation: modelField("updates"),
    Subscription: modelField("subscriptions")
  },
  keys: {
    ErpWell: () => null,
    ERPCustomer: customer => customer.customer_erp_id || null,
    ERPWellAddress: wellAddress =>
      wellAddress.well_erp_id || wellAddress.address_erp_id || null,
    JobType: () => null,
    SKAddress: address => address.id || null,
    SKBillable: billable => billable.id ? String(billable.id) : null,
    SKCoordinates: () => null,
    SKContract: contract => contract.number || null,
    SKCountry: country => country.code || null,
    SKCurrency: currency => currency.code || null,
    SKCustomer: customer => customer.id ? String(customer.id) : null,
    SKEquipmentTypeConversion: conversion =>
      conversion.id ? String(conversion.id) : null,
    SKCableInventory: () => null,
    SKInventory: () => null,
    SKJob: job => job.id ? String(job.id) : null,
    SKMaterial: material => material.number || null,
    SKMaterialPricing: () => null,
    SKMaterialsPaginated: () => null,
    SKNwa: nwa => nwa.id || null,
    SKPlanningOrder: order => order.planning_order || null,
    SKPlant: plant => plant.id || null,
    SKPriceList: priceList => priceList.number || priceList.name || null,
    SKQuote: quote => quote.id ? String(quote.id) : null,
    SKDeleteQuote: quote => quote.id ? String(quote.id) : null,
    SKMaterialDateRange: dateRange =>
      dateRange?.lower && dateRange?.upper
        ? `${dateRange.lower}-${dateRange.upper}`
        : null,
    SKRegion: region => region.id ? String(region.id) : region.code || null,
    SKWell: well => well.id || null,
    SKWellAddress: wellAddress =>
      wellAddress.well_address_id ? String(wellAddress.well_address_id) : null,
    SKWellAddressCustomer: customer => customer.id || null,
    deviation_survey_directional_dataset: dataset => dataset.value || null,
    well_test_gas_input_options: opts => opts.value || null,
    legacy_users: legacy_user => legacy_user.hal_identifier || null,
    scenarios_aggregate: () => null,
    scenarios_aggregate_fields: () => null,
    performance_evaluations_aggregate: () => null
  }
});
